"use client";

import { useState } from "react";
import { Check } from "lucide-react";
import { useCommsPanel } from "../CommsProvider";

interface CaptureFieldProps {
  fieldKey: string;
  label: string;
  fieldType: string;
  options: unknown | null;
  placeholder: string | null;
  mandatory: boolean;
  initialValue?: unknown;
}

export function CaptureField({ fieldKey, label, fieldType, options, placeholder, mandatory, initialValue }: CaptureFieldProps) {
  const { activeLeadId } = useCommsPanel();
  const [value, setValue] = useState<unknown>(initialValue ?? (fieldType === "boolean" ? null : ""));
  const [captured, setCaptured] = useState(initialValue != null && initialValue !== "");
  const [saving, setSaving] = useState(false);

  async function saveValue(newValue: unknown) {
    if (!activeLeadId) return;
    setSaving(true);
    try {
      await fetch(`/api/leads/${activeLeadId}/qualification`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [fieldKey]: newValue }),
      });
      setCaptured(true);
    } finally {
      setSaving(false);
    }
  }

  const optionsList = Array.isArray(options) ? options as string[] : [];

  if (fieldType === "boolean") {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-700">{label}{mandatory && <span className="text-red-500">*</span>}</span>
        <div className="flex gap-1">
          <button
            onClick={() => { setValue(true); saveValue(true); }}
            className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
              value === true ? "bg-green-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            Yes
          </button>
          <button
            onClick={() => { setValue(false); saveValue(false); }}
            className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
              value === false ? "bg-red-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            No
          </button>
        </div>
        {captured && <Check className="h-3.5 w-3.5 text-green-500" />}
      </div>
    );
  }

  if (fieldType === "select") {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-700">{label}{mandatory && <span className="text-red-500">*</span>}</span>
          {captured && <Check className="h-3.5 w-3.5 text-green-500" />}
        </div>
        <select
          value={value as string}
          onChange={(e) => { setValue(e.target.value); saveValue(e.target.value); }}
          className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
        >
          <option value="">{placeholder ?? "Select..."}</option>
          {optionsList.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }

  if (fieldType === "multi_select") {
    const selectedValues = Array.isArray(value) ? value as string[] : [];
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-700">{label}{mandatory && <span className="text-red-500">*</span>}</span>
          {captured && <Check className="h-3.5 w-3.5 text-green-500" />}
        </div>
        <div className="flex flex-wrap gap-1">
          {optionsList.map((opt) => {
            const isSelected = selectedValues.includes(opt);
            return (
              <button
                key={opt}
                onClick={() => {
                  const updated = isSelected ? selectedValues.filter((v) => v !== opt) : [...selectedValues, opt];
                  setValue(updated);
                  saveValue(updated);
                }}
                className={`rounded px-2 py-0.5 text-[10px] font-medium transition-colors ${
                  isSelected ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // text_input or number
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <span className="text-xs text-gray-700">{label}{mandatory && <span className="text-red-500">*</span>}</span>
        {captured && <Check className="h-3.5 w-3.5 text-green-500" />}
      </div>
      <div className="flex gap-1">
        <input
          type={fieldType === "number" ? "number" : "text"}
          value={value as string}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder ?? ""}
          className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs"
        />
        <button
          onClick={() => saveValue(fieldType === "number" ? Number(value) : value)}
          disabled={!value || saving}
          className="rounded bg-blue-500 px-2 py-1 text-xs font-medium text-white hover:bg-blue-600 disabled:opacity-50"
        >
          {saving ? "..." : "Capture"}
        </button>
      </div>
    </div>
  );
}
